import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, Globe, Clock, ChevronRight, CheckCircle } from 'lucide-react';
import PageWrapper from '../components/PageWrapper';
import Breadcrumbs from '../components/Breadcrumbs';
import MedicalText from '../components/MedicalText';
import { standaloneArticles } from '../data/standaloneArticles';

// Standalone Articles Index Page
export const StandaloneArticlesIndex = () => {
  const navigate = useNavigate();

  return (
    <PageWrapper>
      <Breadcrumbs items={[
        { label: 'Education Library', path: '/education' },
        { label: 'Articles', path: '/articles' }
      ]} />

      <div className="page-header-centered" data-testid="articles-index">
        <h1>Articles</h1>
        <p>In-depth patient education articles on specific urological topics</p>
      </div>

      <div className="concerns-list" data-testid="articles-list">
        {standaloneArticles.map(article => (
          <Link 
            to={`/articles/${article.id}`}
            key={article.id}
            className="concern-list-item"
            data-testid={`article-item-${article.id}`}
          >
            <div className="concern-item-content">
              <h3>{article.title.en}</h3>
              <p>{article.content.en.substring(0, 150).trim()}...</p>
            </div>
            <ChevronRight size={20} className="concern-item-arrow" />
          </Link>
        ))}
      </div>
    </PageWrapper>
  );
};

// Individual Article Page
const StandaloneArticle = () => {
  const { articleId } = useParams();
  const navigate = useNavigate();
  const [language, setLanguage] = useState('en');

  // Scroll to top when article loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [articleId]);
  
  const article = standaloneArticles.find(a => a.id === articleId);

  if (!article) {
    return (
      <PageWrapper>
        <div className="empty-state">
          <h2>Article Not Found</h2>
          <p>The article you&apos;re looking for doesn&apos;t exist.</p>
          <button 
            onClick={() => navigate('/education')} 
            className="btn-primary"
          >
            Back to Education Library
          </button>
        </div>
      </PageWrapper>
    );
  }

  const toggleLanguage = () => {
    setLanguage(lang => lang === 'en' ? 'es' : 'en');
  };

  // Calculate reading time
  const wordCount = article.content.en.split(' ').length;
  const readingTime = Math.ceil(wordCount / 200);

  // Map topic IDs to paths
  const topicPaths = {
    'kidney-cancer': { path: '/cancer/kidney-cancer', title: 'Kidney Cancer' },
    'kidney': { path: '/anatomy/kidney', title: 'Kidney Anatomy' },
    'prostate': { path: '/anatomy/prostate', title: 'Prostate Anatomy' },
    'bladder': { path: '/anatomy/bladder', title: 'Bladder Anatomy' }
  };

  // Parse inline bold text (converts **text** to <strong>text</strong>)
  const parseBoldText = (text) => {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, idx) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={idx}>{part.slice(2, -2)}</strong>;
      }
      return <MedicalText key={idx}>{part}</MedicalText>;
    });
  };

  // Render content with markdown-style formatting
  const renderContent = (content) => {
    const lines = content.split('\n');
    const elements = [];
    let currentParagraph = [];

    lines.forEach((line, index) => {
      const trimmedLine = line.trim();
      
      // Handle horizontal divider
      if (trimmedLine === '---') {
        if (currentParagraph.length > 0) {
          elements.push(
            <p key={`p-${index}`}>{parseBoldText(currentParagraph.join(' '))}</p>
          );
          currentParagraph = [];
        }
        elements.push(<hr key={`hr-${index}`} className="article-divider" />);
        return;
      }
      
      // Handle empty lines (paragraph break)
      if (trimmedLine === '') {
        if (currentParagraph.length > 0) {
          elements.push(
            <p key={`p-${index}`}>{parseBoldText(currentParagraph.join(' '))}</p>
          );
          currentParagraph = [];
        }
        return;
      }
      
      // Handle bold header lines (entire line is **text**)
      if (trimmedLine.startsWith('**') && trimmedLine.endsWith('**') && !trimmedLine.slice(2, -2).includes('**')) {
        if (currentParagraph.length > 0) {
          elements.push(
            <p key={`p-${index}`}>{parseBoldText(currentParagraph.join(' '))}</p>
          );
          currentParagraph = [];
        }
        const headerText = trimmedLine.slice(2, -2);
        elements.push(
          <h3 key={`h-${index}`} className="article-section-header">
            <MedicalText>{headerText}</MedicalText>
          </h3>
        );
        return;
      }
      
      // Handle bullet points
      if (trimmedLine.startsWith('•') || trimmedLine.startsWith('- ')) {
        if (currentParagraph.length > 0) {
          elements.push(
            <p key={`p-${index}`}>{parseBoldText(currentParagraph.join(' '))}</p>
          );
          currentParagraph = [];
        }
        const bulletText = trimmedLine.replace(/^[•-]\s*/, '');
        elements.push(
          <div key={`li-${index}`} className="article-list-item">
            <span className="list-bullet">•</span>
            <span>{parseBoldText(bulletText)}</span>
          </div>
        );
        return;
      }
      
      // Regular text - add to current paragraph
      currentParagraph.push(trimmedLine);
    });

    // Don't forget remaining paragraph
    if (currentParagraph.length > 0) {
      elements.push(
        <p key="p-final">{parseBoldText(currentParagraph.join(' '))}</p>
      );
    }

    return elements;
  };

  return (
    <PageWrapper>
      <Breadcrumbs items={[
        { label: 'Education Library', path: '/education' },
        { label: article.title.en, path: `/articles/${article.id}` }
      ]} />

      <article className="article-full cancer-article" data-testid="standalone-article">
        {/* Header */}
        <header className="cancer-article-header">
          <div className="cancer-ribbon">🎗️</div>
          <h1>{article.title[language]}</h1>
          <div className="article-meta">
            <span><Clock size={16} /> {readingTime} min read</span>
            <button 
              onClick={toggleLanguage}
              className="language-btn"
              data-testid="language-toggle"
            >
              <Globe size={16} />
              {language === 'en' ? 'Ver en Español' : 'View in English'}
            </button>
          </div>
        </header>

        {/* Key Points Box */}
        {article.keyPoints && (
          <div className="key-points-box" data-testid="key-points">
            <h3>{language === 'en' ? 'Key Points' : 'Puntos Clave'}</h3>
            <ul>
              {article.keyPoints[language].map((point, idx) => (
                <li key={idx}>
                  <CheckCircle size={16} className="check-icon" />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Article Image */}
        {article.image && (
          <figure className="article-image-container" data-testid="article-image">
            <img 
              src={article.image.url} 
              alt={article.image.alt[language]} 
              className="article-image"
            />
            <figcaption className="article-image-caption">
              <span className="caption-text">{article.image.caption[language]}</span>
              <span className="caption-source">Source: {article.image.source}</span>
            </figcaption>
          </figure>
        )}

        {/* Main Content */}
        <div className="article-body">
          {renderContent(article.content[language])}
        </div>

        {/* When to Call Your Doctor Box */}
        {article.whenToCall && (
          <div className="when-to-call-box" data-testid="when-to-call">
            <h3>
              <AlertTriangle size={20} />
              {language === 'en' ? 'When to Call Your Doctor' : 'Cuándo Llamar a Su Médico'}
            </h3>
            <ul>
              {article.whenToCall[language].map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Important Note */}
        <div className="important-note">
          <AlertTriangle size={20} />
          <p>
            {language === 'en' 
              ? 'This information is for educational purposes only. Always discuss your specific situation with your healthcare provider.'
              : 'Esta información es solo para fines educativos. Siempre discuta su situación específica con su proveedor de atención médica.'}
          </p>
        </div>

        {/* Related Topics */}
        {article.relatedTopics && article.relatedTopics.length > 0 && (
          <div className="related-topics">
            <h3>{language === 'en' ? 'Related Topics' : 'Temas Relacionados'}</h3>
            <div className="related-topics-list">
              {article.relatedTopics.map(topicId => {
                const topic = topicPaths[topicId];
                if (!topic) return null;
                return (
                  <Link 
                    key={topicId}
                    to={topic.path}
                    className="related-topic-link"
                  >
                    {topic.title}
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* Back Button */}
        <div className="article-nav">
          <button 
            onClick={() => navigate('/education')} 
            className="btn-back"
            data-testid="back-button"
          >
            <ArrowLeft size={16} />
            Back to Education Library
          </button>
        </div>
      </article>
    </PageWrapper>
  );
};

export default StandaloneArticle;
